<template>
  <div class="body-wrapper">
    <div class="body-content">
      <h1>
        <span
          class="icon backbutton"
          @click="
            $router.push({
              name: 'eventSummary',
              params: { id: event.id }
            })
          "
        >
          <i class="fas fa-chevron-left"></i>
        </span>
        {{ event.title }}
      </h1>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h3>{{ dateSummary.startDate }}</h3>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <button class="button" @click="exportCsv">Export CSV</button>
          </div>
        </div>
      </div>
      <table id="eventTable" class="table">
        <thead :key="sortColumn + sortDirection">
          <tr>
            <th class="sortable" @click="sortBy('firstName')">
              First Name
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'firstName'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('lastName')">
              Last Name
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'lastName'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('email')">
              Email
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'email'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('quantity')">
              Quantity
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'quantity'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('checkedin')">
              Checkedin
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'checkedin'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('tierName')">
              Price Tier
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'tierName'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable" @click="sortBy('discountCode')">
              Discount Code
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'discountCode'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable has-text-right" @click="sortBy('price')">
              Price Each
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'price'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="sortable has-text-right" @click="sortBy('total')">
              Gross Total
              <span class="sort-icon">
                <i
                  class="fas"
                  :class="
                    sortColumn === 'total'
                      ? sortDirection === 'asc'
                        ? 'fa-sort-up'
                        : 'fa-sort-down'
                      : 'fa-sort sort-inactive'
                  "
                ></i>
              </span>
            </th>
            <th class="has-text-right">Net After Fee</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="patron in sortedAudience"
            :key="patron.orderId"
            class="clickable-row"
            @click="showOrderDetails(patron)"
          >
            <td>{{ patron.firstName }}</td>
            <td>{{ patron.lastName }}</td>
            <td>{{ patron.email }}</td>
            <td>{{ patron.quantity }}</td>
            <td>{{ patron.checkedin }}</td>
            <td>{{ patron.tierName }}</td>
            <td>{{ patron.discountCode }}</td>
            <td class="has-text-right">${{ patron.price }}</td>
            <td class="has-text-right">${{ patron.total }}</td>
            <td class="has-text-right">${{ getNet(patron.total) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th>{{ totalQty }}</th>
            <th>{{ totalCheckedin }}</th>
            <th colspan="3"></th>
            <th class="has-text-right">${{ totalDollars }}</th>
            <th class="has-text-right">${{ totalNet }}</th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "@/store";

export default {
  props: ["eventId", "id"],
  data() {
    return {
      sortColumn: "lastName",
      sortDirection: "asc"
    };
  },
  computed: {
    ...mapGetters({
      dateSummary: "dateSummary",
      audience: "audience",
      event: "event"
    }),
    sortedAudience() {
      if (!this.audience || this.audience.length === 0) return [];
      const numericColumns = ["quantity", "checkedin", "price", "total"];
      const isNumeric = numericColumns.includes(this.sortColumn);

      return [...this.audience].sort((a, b) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];

        if (isNumeric) {
          valA = Number(valA) || 0;
          valB = Number(valB) || 0;
        } else {
          valA = (valA || "").toString().toLowerCase();
          valB = (valB || "").toString().toLowerCase();
        }

        let comparison = 0;
        if (valA < valB) comparison = -1;
        else if (valA > valB) comparison = 1;

        return this.sortDirection === "asc" ? comparison : -comparison;
      });
    },
    totalQty() {
      if (this.audience.length > 0)
        return this.audience
          .map(patron => patron.quantity)
          .reduce((prev, next) => prev + next);
      else return 0;
    },
    totalCheckedin() {
      if (this.audience.length > 0)
        return this.audience
          .map(patron => patron.checkedin)
          .reduce((prev, next) => prev + next);
      else return 0;
    },
    totalDollars() {
      if (this.audience.length > 0)
        return this.audience
          .map(patron => patron.total)
          .reduce((prev, next) => prev + next);
      else return 0;
    },
    totalNet() {
      if (this.audience.length > 0)
        return (
          this.audience
            .map(patron => patron.total)
            .reduce((prev, next) => prev + next) * 0.95
        ).toFixed(2);
      else return 0;
    }
  },
  methods: {
    sortBy(column) {
      console.log("sortBy called with:", column);
      console.log(
        "Before - sortColumn:",
        this.sortColumn,
        "sortDirection:",
        this.sortDirection
      );
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortDirection = "asc";
      }
      console.log(
        "After - sortColumn:",
        this.sortColumn,
        "sortDirection:",
        this.sortDirection
      );
    },
    exportCsv() {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent +=
        "First Name,Last Name,Email,OrderID,Status,Quantity,Checked In,Tier Name,Discount Code,Price,Total,Total After Fees\r\n";
      this.audience.forEach(function(line) {
        const net = (Math.round(line.total * 95) / 100).toFixed(2);
        csvContent +=
          line.firstName +
          "," +
          line.lastName +
          "," +
          line.email +
          "," +
          line.orderId +
          "," +
          line.status +
          "," +
          line.quantity +
          "," +
          line.checkedin +
          "," +
          line.tierName +
          "," +
          line.discountCode +
          "," +
          line.price +
          "," +
          line.total +
          "," +
          net +
          "\r\n";
      });
      // console.log(csvContent);
      const exportname =
        this.event.title.replace(/ /g, "") + this.dateSummary.startDate;
      var encodedUri = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", exportname + ".csv");
      document.body.appendChild(link); // Required for FF

      link.click();
    },
    getNet(ammount) {
      return (Math.round(ammount * 95) / 100).toFixed(2);
    },
    showOrderDetails(patron) {
      this.$parent.orderdetails(patron, this.eventId);
    }
  },
  async created() {
    console.log("Get event summarys.", {
      eventId: this.eventId,
      dateId: this.id
    });
    await store.dispatch("fetchEvent", this.eventId);
    await store.dispatch("fetchDateSummary", {
      eventId: this.eventId,
      dateId: this.id
    });
    await store.dispatch("fetchAudience", {
      eventId: this.eventId,
      dateId: this.id
    });
  }
};
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: $gray6;
  }

  &:active {
    background-color: $gray5;
  }
}

.table {
  .clickable-row:hover td {
    background-color: $gray6;
  }
}

th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    background-color: $gray6;
  }
}

.sort-icon {
  margin-left: 0.5em;

  .sort-inactive {
    color: $gray4;
  }
}
</style>
